/* eslint-disable @typescript-eslint/no-explicit-any */
import useSWR, { SWRConfiguration } from 'swr';
import axios from 'axios';
import { useClientEnvVarsStore } from '@marriott/mi-store-utils';
import { getItemFromPageModel, getMockPageModel } from '../../utils/src/helper';
import { respGridUtil } from '@marriott/mi-headless-utils';
import { logger } from '../../utils';
import { SWR_ERROR_RETRY_COUNT, SWR_ERROR_RETRY_INTERVAL } from '../../constants/lib/constants';

const { IS_LOCAL_DEV } = process.env;
const { log } = logger({})('useHQVPageModel');

export interface HQVPageModelRequestBody {
  requestType: string;
  seoUrl: string;
  localeKey: string;
  experienceSegment: string | string[];
  applicationName: string;
  sessionToken: string;
}

export interface UseHQVPageModelOptions {
  enabled?: boolean;
  swrConfig?: SWRConfiguration;
}

export interface UseHQVPageModelResult {
  pageModel: any;
  responsiveGridModel: any;
  responsiveGridModelProps: any;
  isLoading: boolean;
  isValidating: boolean;
  error: Error | undefined;
  refetch: () => Promise<any>;
}

/**
 * Fetches the AEM page model from the CMS template API
 */
async function fetchPageModel(templateURL: string, reqBody: HQVPageModelRequestBody, requestId?: string): Promise<any> {
  log.debug('Fetching AEM page model from:', templateURL);
  try {
    const response = await axios.post(templateURL, reqBody, {
      headers: {
        'x-request-id': requestId || '',
        'accept-language': reqBody.localeKey || 'en-US',
      },
    });
    if (response?.data?.data) {
      return response.data.data;
    }
    return {};
  } catch (err) {
    log.debug('Error fetching AEM page model:', err);
    throw err;
  }
}

/**
 * Custom SWR hook for fetching HQV AEM Page Model (getCMSTemplate)
 *
 * This hook pre-fetches the AEM page model on page load so the HQV modal
 * can render immediately when opened, without waiting for the layout to load.
 *
 * @param templateURL - The URL to fetch the CMS template from
 * @param reqBody - The request body for getCMSTemplate API
 * @param itemPath - The item path to extract from the page model
 * @param pageName - The page name for mock data (local dev only)
 * @param options - Configuration options
 * @returns Page model data, grid model, grid props, loading state, error, and refetch function
 *
 * @example
 * ```tsx
 * const { pageModel, responsiveGridModel, responsiveGridModelProps, isLoading } = useHQVPageModel(
 *   templateURL,
 *   getCMSTemplateReqBody,
 *   ITEM_PATH_ROOT,
 *   PAGE_NAME
 * );
 *
 * // Pass to PageonModal
 * <PageonModal
 *   prefetchedModel={pageModel}
 *   prefetchedGridModel={responsiveGridModel}
 *   prefetchedGridProps={responsiveGridModelProps}
 *   ...
 * />
 * ```
 */
export function useHQVPageModel(
  templateURL: string | null,
  reqBody: HQVPageModelRequestBody | null,
  itemPath: string,
  pageName: string,
  options: UseHQVPageModelOptions = {}
): UseHQVPageModelResult {
  const { enabled = true, swrConfig = {} } = options;
  const { envVarsObject } = useClientEnvVarsStore();
  const requestId = envVarsObject?.['REQUEST_ID'];

  const shouldFetch = enabled && !!templateURL && !!reqBody;

  const { data, error, isLoading, isValidating, mutate } = useSWR(
    shouldFetch ? ['hqv-page-model', reqBody?.localeKey, reqBody?.sessionToken] : null,
    async () => {
      log.debug('Fetching HQV page model');

      let template: any;
      if (IS_LOCAL_DEV === 'true') {
        template = await getMockPageModel(pageName);
      } else {
        template = await fetchPageModel(templateURL!, reqBody!, requestId);
      }

      // Process the template to get grid model and props
      const gridModel = getItemFromPageModel(template, itemPath);
      const gridProps = respGridUtil(gridModel);

      return {
        pageModel: template,
        responsiveGridModel: gridModel,
        responsiveGridModelProps: gridProps,
      };
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      // Long cache interval (5 minutes) since AEM layout rarely changes
      dedupingInterval: 300000,
      errorRetryCount: SWR_ERROR_RETRY_COUNT,
      errorRetryInterval: SWR_ERROR_RETRY_INTERVAL,
      ...swrConfig,
    }
  );

  const refetch = async () => {
    log.debug('Refetching HQV page model');
    return mutate();
  };

  return {
    pageModel: data?.pageModel,
    responsiveGridModel: data?.responsiveGridModel,
    responsiveGridModelProps: data?.responsiveGridModelProps,
    isLoading,
    isValidating,
    error,
    refetch,
  };
}
